import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';

interface LeadAlerta {
  contato: string | null;
  telefone: string | null;
  palavras: string[];
  total_mensagens: number;
  ultima_mensagem: string | null;
  ultima_data: string | null;
  convertido: boolean;
}

const PALAVRA_CORES: Record<string, string> = {
  'Obra': 'bg-amber-100 text-amber-800 border-amber-200',
  'Orçamento': 'bg-green-100 text-green-800 border-green-200',
};

interface AlertasLeadProps {
  password: string;
  onChanged?: () => void;
}

function formatDataHora(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AlertasLead({ password, onChanged }: AlertasLeadProps) {
  const [leads, setLeads] = useState<LeadAlerta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [convertendo, setConvertendo] = useState<string | null>(null);
  const [feedMsg, setFeedMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('crm-whatsapp', {
        body: { action: 'leads', password },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      const parsed = data as { leads?: LeadAlerta[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setLeads(parsed?.leads ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar alertas de lead.');
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => {
    load();
  }, [load]);

  const converter = async (lead: LeadAlerta) => {
    const chave = lead.telefone ?? lead.contato ?? '';
    setConvertendo(chave);
    setFeedMsg(null);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('crm-whatsapp', {
        body: {
          action: 'converter_lead',
          password,
          contato: lead.contato,
          telefone: lead.telefone,
          palavras: lead.palavras,
          ultima_mensagem: lead.ultima_mensagem,
        },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      if (!parsed?.ok) throw new Error('Falha ao converter lead.');
      setFeedMsg(`"${lead.contato || lead.telefone}" virou lead no CRM. Confira na aba Leads.`);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao converter lead.');
    } finally {
      setConvertendo(null);
    }
  };

  const pendentes = leads.filter((l) => !l.convertido);
  const convertidos = leads.filter((l) => l.convertido);

  return (
    <div className="bg-white rounded-2xl border border-hop-border p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
            <i className="ri-alarm-warning-line text-lg"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Alertas de lead</h3>
            <p className="text-sm text-gray-500">
              Contatos que falaram de <strong>obra</strong> ou <strong>orçamento</strong> no WhatsApp — prontos pra virar negociação.
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#E8610A]/10 text-[#E8610A] border border-[#E8610A]/20 rounded-full px-3 py-1">
          <i className="ri-fire-line"></i>
          {pendentes.length} lead{pendentes.length === 1 ? '' : 's'} em potencial
        </span>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</div>
      )}
      {feedMsg && (
        <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4">
          <i className="ri-check-double-line mr-1"></i>
          {feedMsg}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
            <i className="ri-alarm-warning-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500">
            Nenhum contato falou de obra ou orçamento ainda. Importe conversas pra detecção automática.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendentes.map((lead) => (
            <LeadCard
              key={lead.telefone ?? lead.contato ?? 'x'}
              lead={lead}
              convertendo={convertendo}
              onConverter={() => converter(lead)}
            />
          ))}

          {convertidos.length > 0 && (
            <>
              <div className="flex items-center gap-2 pt-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Já convertidos
                </span>
                <span className="h-px flex-1 bg-gray-100"></span>
              </div>
              {convertidos.map((lead) => (
                <LeadCard
                  key={lead.telefone ?? lead.contato ?? 'y'}
                  lead={lead}
                  convertendo={convertendo}
                  onConverter={() => converter(lead)}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function LeadCard({
  lead,
  convertendo,
  onConverter,
}: {
  lead: LeadAlerta;
  convertendo: string | null;
  onConverter: () => void;
}) {
  const chave = lead.telefone ?? lead.contato ?? '';
  const isConverting = convertendo === chave;

  return (
    <div
      className={`rounded-xl border p-4 flex items-start justify-between gap-4 flex-wrap ${
        lead.convertido ? 'border-gray-200 bg-gray-50/60' : 'border-[#E8610A]/30 bg-[#E8610A]/5'
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-semibold text-gray-900">{lead.contato || 'Contato sem nome'}</h4>
          {lead.telefone && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
              <i className="ri-phone-line"></i>
              {lead.telefone}
            </span>
          )}
          {lead.palavras.map((p) => (
            <span
              key={p}
              className={`inline-flex items-center text-[10px] font-semibold rounded-full px-2 py-0.5 border ${PALAVRA_CORES[p] ?? 'bg-gray-100 text-gray-700 border-gray-200'}`}
            >
              {p}
            </span>
          ))}
        </div>

        {lead.ultima_mensagem && (
          <p className="text-sm text-gray-600 mt-1.5 line-clamp-2 max-w-2xl">
            “{lead.ultima_mensagem}”
          </p>
        )}

        <div className="flex items-center gap-3 text-xs text-gray-400 mt-2 flex-wrap">
          <span>
            <i className="ri-message-3-line mr-1"></i>
            {lead.total_mensagens} mensagem(ns) relevantes
          </span>
          {lead.ultima_data && (
            <span>
              <i className="ri-time-line mr-1"></i>
              {formatDataHora(lead.ultima_data)}
            </span>
          )}
        </div>
      </div>

      {lead.convertido ? (
        <span className="inline-flex items-center gap-1 text-xs font-semibold bg-green-50 text-green-700 border border-green-200 rounded-full px-3 py-1.5 whitespace-nowrap shrink-0">
          <i className="ri-check-double-line"></i>
          Já no CRM
        </span>
      ) : (
        <button
          onClick={onConverter}
          disabled={isConverting}
          className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#E8610A] px-4 py-2 rounded-lg hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 shrink-0"
        >
          {isConverting ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Convertendo...
            </>
          ) : (
            <>
              <i className="ri-user-add-line"></i>
              Virar lead no CRM
            </>
          )}
        </button>
      )}
    </div>
  );
}